import React, { useEffect, useState } from 'react';
import { buttonVariants } from '@/ui/components/button';
import { ChevronRightIcon } from '@/ui/icons';
import { cn } from '@/ui/lib/utils';
import * as projectClient from '../../models/projects/projectClient';
import { ANGEL_TYPE, type Project } from '../../models/projects/project';
import { handleHttpError } from '../../../common/http/errorHandler';
import AngelTaxProjectCard from './angelTaxProjectCard';

interface Props {
  // 案件一覧（募集ページ一覧の「全て」）への遷移先。エンジェル税制対象の案件は少なく、募集中に絞ると
  // 「他を見る」が成り立たないため、絞り込みなしの一覧へ送る
  projectsPath: string;
}

// 表示する案件の最大数。lg 以上は 3 列 × 2 行、それ未満は先頭 4 件（Figma の注記「spでは最大カード数4枚」）。
// 1 列のときカードを幅いっぱいにするのは Figma（312px 固定）と異なる実装側の判断
const PREVIEW_SIZE = 6;
const SP_PREVIEW_SIZE = 4;

// シミュレーション結果の下にエンジェル税制対象の募集中案件を並べる。API（angel_tax=1）が絞るが、
// 対象外が混ざらないことをこの画面でも保証するため angel_type で filter する。0 件なら一覧を出さずボタンだけ
export default function AngelTaxProjectsSection({ projectsPath }: Props) {
  // null は取得前。取得失敗時は空にして「対象案件なし」と同じ表示にする
  const [projects, setProjects] = useState<Project[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    projectClient
      .fetchAngelTax(PREVIEW_SIZE, controller.signal)
      .then(projects => setProjects(projects))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // 一覧はシミュレーターの付随情報なので、利用者には通知せず Sentry にだけ送る
        setProjects([]);
        handleHttpError(error);
      });
    return () => controller.abort();
  }, []);

  if (projects == null) return null;

  const angelTaxProjects = projects.filter(
    project => project.angelType !== ANGEL_TYPE.none,
  );

  return (
    <div className="tw:flex tw:w-full tw:max-w-245 tw:flex-col tw:items-center tw:gap-5 tw:lg:gap-10">
      {angelTaxProjects.length > 0 && (
        <>
          <h2 className="tw:m-0 tw:text-center tw:text-xl tw:leading-normal tw:font-semibold tw:tracking-text tw:md:text-3xl">
            エンジェル税制対象案件は
            <br />
            こちら
          </h2>
          <ul className="tw:m-0 tw:grid tw:w-full tw:list-none tw:grid-cols-1 tw:justify-items-center tw:gap-x-5 tw:gap-y-4 tw:p-0 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:lg:gap-y-10">
            {angelTaxProjects.map((project, index) => (
              <li
                key={project.id}
                className={cn(
                  'tw:w-full tw:md:max-w-78',
                  index >= SP_PREVIEW_SIZE && 'tw:hidden tw:lg:block',
                )}
              >
                <AngelTaxProjectCard project={project} />
              </li>
            ))}
          </ul>
        </>
      )}
      {/* リンクは Button でなく a に buttonVariants を当てる（Button は描画した要素に type="button" か role="button" を付けるため）。
          https://ui.shadcn.com/docs/components/base/button#as-link */}
      <a
        href={projectsPath}
        className={cn(
          buttonVariants({ size: 'lg' }),
          'tw:h-13 tw:w-65 tw:font-noto tw:md:h-15 tw:md:w-81',
        )}
      >
        案件一覧を見る
        <ChevronRightIcon data-icon="end" />
      </a>
    </div>
  );
}
